import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useRouter } from 'src/routes/hooks';

import { checkRedirectNeeded } from 'src/utils/auto-redirect';

import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

/**
 * 🔄 COMPONENTE DE REDIRECCIÓN AUTOMÁTICA
 * Redirige usuarios a su primera página accesible si no pueden acceder al dashboard principal
 */
const AutoRedirectGuard = ({ children, currentRoute = '/medicore' }) => {
  const { user } = useAuthContext();
  const router = useRouter();

  useEffect(() => {
    if (!user) return;

    const redirectRoute = checkRedirectNeeded(user, currentRoute);

    if (redirectRoute && redirectRoute !== currentRoute) {
      router.replace(redirectRoute);
    }
  }, [user, currentRoute, router]);

  return children;
};

AutoRedirectGuard.propTypes = {
  children: PropTypes.node.isRequired,
  currentRoute: PropTypes.string,
};

export default AutoRedirectGuard;

// ----------------------------------------------------------------------

/**
 * 🔄 HOC para redirección automática
 * @param {string} currentRoute - Ruta actual que se está protegiendo
 * @returns {Function} - Componente envuelto con redirección automática
 */
export const withAutoRedirect = (currentRoute) => (WrappedComponent) => {
  const RedirectWrappedComponent = (props) => (
    <AutoRedirectGuard currentRoute={currentRoute}>
      <WrappedComponent {...props} />
    </AutoRedirectGuard>
  );

  RedirectWrappedComponent.displayName = `withAutoRedirect(${WrappedComponent.displayName || WrappedComponent.name})`;

  return RedirectWrappedComponent;
};
